/**
 * Before/After Image Comparison Component
 * Handles mouse and touch interactions for image comparison slider
 */

class BeforeAfter extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.isVertical = this.hasAttribute('vertical');
    this.cursor = this.querySelector('.before-after__cursor');
    this.beforeImage = this.querySelector('.before-after__before-image');
    this.afterImage = this.querySelector('.before-after__after-image');
    
    if (this.cursor && this.beforeImage) {
      this.init();
    }
  }

  init() {
    // Get initial position from CSS custom property
    const initialPosition = this.getInitialPosition();
    this.setPosition(initialPosition);

    // Add event listeners
    this.addEventListeners();

    // Set up keyboard navigation
    this.setupKeyboardNavigation();
  }

  getInitialPosition() {
    const computedStyle = getComputedStyle(this);
    const position = computedStyle.getPropertyValue('--before-after-initial-cursor-position');
    return parseFloat(position) || 50;
  }

  addEventListeners() {
    // Mouse events
    this.addEventListener('mousedown', this.handleStart.bind(this));
    document.addEventListener('mousemove', this.handleMove.bind(this));
    document.addEventListener('mouseup', this.handleEnd.bind(this));

    // Touch events
    this.addEventListener('touchstart', this.handleStart.bind(this));
    document.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
    document.addEventListener('touchend', this.handleEnd.bind(this));

    // Prevent context menu on long press
    this.addEventListener('contextmenu', (e) => e.preventDefault());

    // Handle resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  setupKeyboardNavigation() {
    this.cursor.addEventListener('keydown', (e) => {
      const step = 5; // 5% steps
      let currentPosition = this.getCurrentPosition();

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          this.setPosition(Math.max(0, currentPosition - step));
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          this.setPosition(Math.min(100, currentPosition + step));
          break;
        case 'Home':
          e.preventDefault();
          this.setPosition(0);
          break;
        case 'End':
          e.preventDefault();
          this.setPosition(100);
          break;
      }
    });
  }

  handleStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.classList.add('is-dragging');
    
    // Disable text selection during drag
    document.body.style.userSelect = 'none';
    
    // Add visual feedback
    this.cursor.style.transform = this.isVertical 
      ? 'translateY(-50%) scale(1.1)' 
      : 'translateX(-50%) scale(1.1)';
  }

  handleMove(e) {
    if (!this.isDragging) return;

    e.preventDefault();
    
    const rect = this.getBoundingClientRect();
    let position;

    if (this.isVertical) {
      const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
      position = ((clientY - rect.top) / rect.height) * 100;
    } else {
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
      position = ((clientX - rect.left) / rect.width) * 100;
    }

    // Clamp position between 0 and 100
    position = Math.max(0, Math.min(100, position));
    this.setPosition(position);
  }

  handleEnd(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.classList.remove('is-dragging');
    
    // Re-enable text selection
    document.body.style.userSelect = '';
    
    // Reset visual feedback
    this.cursor.style.transform = this.isVertical 
      ? 'translateY(-50%)' 
      : 'translateX(-50%)';
  }

  handleResize() {
    // Refresh position on resize to maintain accuracy
    const currentPosition = this.getCurrentPosition();
    this.setPosition(currentPosition);
  }

  setPosition(percentage) {
    // Clamp percentage between 0 and 100
    percentage = Math.max(0, Math.min(100, percentage));

    if (this.isVertical) {
      this.beforeImage.style.height = `${percentage}%`;
      this.cursor.style.top = `${percentage}%`;
    } else {
      this.beforeImage.style.width = `${percentage}%`;
      this.cursor.style.left = `${percentage}%`;
    }

    // Update CSS custom property for consistent theming
    this.style.setProperty('--before-after-initial-cursor-position', `${percentage}%`);

    // Dispatch custom event for analytics or other integrations
    this.dispatchEvent(new CustomEvent('beforeAfterChange', {
      detail: { position: percentage },
      bubbles: true
    }));
  }

  getCurrentPosition() {
    if (this.isVertical) {
      return (this.cursor.offsetTop / this.offsetHeight) * 100;
    } else {
      return (this.cursor.offsetLeft / this.offsetWidth) * 100;
    }
  }
}

// Register the custom element
if (!customElements.get('before-after')) {
  customElements.define('before-after', BeforeAfter);
}

// Handle page visibility change to reset any stuck states
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Reset any dragging state when page becomes hidden
    document.querySelectorAll('before-after').forEach(element => {
      if (element.isDragging) {
        element.handleEnd();
      }
    });
  }
});

// Initialize any existing components on page load
document.addEventListener('DOMContentLoaded', () => {
  // This will automatically initialize due to custom element definition
  // But we can add any additional setup here if needed
  
  // Add loading animation for images
  document.querySelectorAll('before-after img').forEach(img => {
    if (!img.complete) {
      img.style.opacity = '0';
      img.addEventListener('load', () => {
        img.style.transition = 'opacity 0.3s ease';
        img.style.opacity = '1';
      });
    }
  });
}); 